import { Component, lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { RouteScrollReset } from "./components/RouteScrollReset";
import { Shell } from "./components/Shell";
import { RouteSectionBanner } from "./components/section-banner/RouteSectionBanner";
import { Loading } from "./components/ui";
import {SupportCacheBoundary} from "./support/SupportCacheBoundary";
import Home from "./pages/Home";
import NotFound, { ErrorPage } from "./pages/NotFound";

const Features = lazy(() => import("./pages/Features"));
const Download = lazy(() => import("./pages/Download"));
const Changelog = lazy(() => import("./pages/changelog/Changelog"));
const Requirements = lazy(() => import("./pages/Requirements"));
const DonationLeaderboard = lazy(() => import("./pages/donate/Leaderboard"));
const Donate = lazy(() => import("./pages/donate/Donate"));
const SupporterCredits = lazy(() => import("./pages/donate/Credits"));
const SupporterVote = lazy(() => import("./pages/donate/Vote"));
const Supporter = lazy(() => import("./pages/account/Supporter"));
const Faq = lazy(() => import("./pages/faq/Faq"));
const Docs = lazy(() => import("./docs/Docs"));
const TicketList = lazy(() => import("./support/TicketList"));
const TicketChat = lazy(() => import("./support/TicketChat"));
const Support = lazy(() => import("./pages/Support"));
const Auth = () => import("./pages/auth/AuthPages");
const Login = lazy(() => Auth().then((m) => ({ default: m.Login })));
const Register = lazy(() => Auth().then((m) => ({ default: m.Register })));
const VerifyEmail = lazy(() => Auth().then((m) => ({ default: m.VerifyEmail })));
const ConfirmEmailChange = lazy(() => Auth().then((m) => ({ default: m.ConfirmEmailChange })));
const ForgotPassword = lazy(() => Auth().then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => Auth().then((m) => ({ default: m.ResetPassword })));
const Mfa = lazy(() => Auth().then((m) => ({ default: m.Mfa })));
const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const Overview = lazy(() => import("./pages/account/Overview"));
const Security = lazy(() => import("./pages/account/Security"));
const Data = lazy(() => import("./pages/account/Data"));
const DesktopAuthorize = lazy(() => import("./pages/DesktopAuthorize"));
const Admin = lazy(() => import("./pages/admin/Admin"));
const Legal = lazy(() => import("./pages/legal/Legal"));
const DevMail = lazy(() => import("./pages/DevMail"));

class Boundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  render() {
    return this.state.error ? <ErrorPage error={this.state.error} /> : this.props.children;
  }
}

export default function App() {
  return (
    <Shell>
      <RouteScrollReset />
      <SupportCacheBoundary />
      <RouteSectionBanner />
      <Boundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/download" element={<Download />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/requirements" element={<Requirements />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/donate/leaderboard" element={<DonationLeaderboard />} />
            <Route path="/donate/credits" element={<SupporterCredits />} />
            <Route path="/donate/vote" element={<SupporterVote />} />
            <Route path="/pricing" element={<Navigate to="/donate" replace />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/docs/*" element={<Docs />} />
            <Route path="/support" element={<Support />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/mfa" element={<Mfa />} />
            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<Overview />} />
              <Route path="security" element={<Security />} />
              <Route path="tickets" element={<TicketList />} />
              <Route path="tickets/:id" element={<TicketChat />} />
              <Route path="data" element={<Data />} />
              <Route path="supporter" element={<Supporter />} />
            </Route>
            <Route path="/desktop/authorize" element={<DesktopAuthorize />} />
            <Route path="/admin/*" element={<Admin />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/dev/mail" element={<DevMail />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Boundary>
    </Shell>
  );
}
